/**
 * The AI omnibar may only navigate and prefill. It must never change stored state - no saving,
 * sending, deleting or billing - and that is enforced here rather than trusted to the model or to
 * whoever writes the next capability.
 *
 * While a capability is running, every request that would mutate the API is refused at the client
 * seams: the REST client (axios), GraphQL (a `mutation` document), and the legacy AngularJS
 * `$http` layer. A capability that tries to write fails loudly and the attempt is surfaced to the
 * user, instead of quietly committing something nobody asked for.
 *
 * This lives in core rather than in the omnibar module because the API clients have to consult it,
 * and core cannot depend on a feature module.
 */

export class AiWriteBlockedError extends Error {
  constructor(public readonly method: string, public readonly path: string) {
    super(
      `The AI omnibar may only navigate and prefill, so it blocked ${method} ${path}. ` +
        `If this action is meant to be possible, it has to be done by the user, not the assistant.`
    );
    this.name = "AiWriteBlockedError";
  }
}

export type AiBlockedWrite = { method: string; path: string };

/**
 * Requests that are infrastructure rather than agency state, and so stay allowed: the omnibar's
 * own planning call, and the auth token refresh (blocking that would log the user out mid-plan).
 *
 * Plus the handful of reads that are POSTs. A page opened by a capability loads itself inside this
 * window, and a read refused here does not look like a refusal - it looks like the page has no
 * data, and then like a filter the page cannot offer. Only a route the server itself documents as
 * a read belongs here: `patient_addresses` is a GET whose id list outgrew the query string, said
 * so in PatientView.ts. A route that writes anything must never be added, however convenient.
 */
const ALWAYS_ALLOWED = [
  /\/ai_omnibar\//,
  /\/auth\/token$/,
  /\/patient_addresses$/,
  // The patient profile's billing section reads its invoices with a POST - InvoiceView.ts, "Agency
  // member requested all patient invoices including related details". Refusing it left the section
  // the assistant had just opened showing "Something went wrong".
  /\/patients\/\d+\/invoices$/,
];

/**
 * A capability's own work can outlive its `run` - a form that saves on blur, say - so the guard
 * stays armed briefly after it returns. Kept short so a person cannot realistically be inside it
 * when they click Save themselves.
 *
 * Not applied to navigation. Opening a screen is *meant* to start requests: the screen loads itself,
 * and several of those loads are POSTs because the query is too long for a URL - a patient's
 * invoices, a list of patient addresses. Holding the guard open across them broke the screen the
 * assistant had just opened, which is a worse outcome than the one the window guards against: a
 * navigation cannot save anything, and anything it opens still has its own guard when it runs.
 */
const TRAILING_WINDOW_MS = 1500;

let depth = 0;
let armedUntil = 0;
let blocked: AiBlockedWrite[] = [];

function isMutatingMethod(method: string): boolean {
  switch (method.toUpperCase()) {
    case "GET":
    case "HEAD":
    case "OPTIONS":
      return false;
    default:
      return true;
  }
}

export function isAiCapabilityRunning(): boolean {
  return depth > 0 || Date.now() < armedUntil;
}

/**
 * Runs a capability with the guard armed. Everything the omnibar executes goes through here.
 *
 * `effect` decides whether the trailing window applies - see TRAILING_WINDOW_MS. A caller that does
 * not say defaults to the strict side.
 */
export async function runGuardedByAiWriteRule<T>(
  run: () => T | Promise<T>,
  effect: "navigate" | "prefill" = "prefill"
): Promise<T> {
  depth++;

  try {
    return await run();
  } finally {
    depth--;

    if (effect !== "navigate") {
      armedUntil = Date.now() + TRAILING_WINDOW_MS;
    }
  }
}

/**
 * Called by each client seam before it sends. Throws when the request would change state while a
 * capability is running.
 */
export function assertAiWriteAllowed(method: string, url: string): void {
  if (!isAiCapabilityRunning() || !isMutatingMethod(method)) {
    return;
  }

  const path = toPath(url);

  if (ALWAYS_ALLOWED.some((allowed) => allowed.test(path))) {
    return;
  }

  blocked = [...blocked, { method: method.toUpperCase(), path }];

  console.error(
    `[ai-omnibar] blocked ${method.toUpperCase()} ${path} - capabilities may only navigate and prefill`
  );

  throw new AiWriteBlockedError(method.toUpperCase(), path);
}

/**
 * Same rule for GraphQL, where a read and a write are both POSTs.
 */
export function assertAiGraphqlAllowed(operation: "query" | "mutation" | "subscription"): void {
  if (!isAiCapabilityRunning() || operation !== "mutation") {
    return;
  }

  blocked = [...blocked, { method: "GRAPHQL", path: "mutation" }];

  console.error(
    "[ai-omnibar] blocked a GraphQL mutation - capabilities may only navigate and prefill"
  );

  throw new AiWriteBlockedError("GRAPHQL", "mutation");
}

/**
 * Drains what was blocked, so the omnibar can tell the user what it refused to do.
 */
export function takeBlockedAiWrites(): AiBlockedWrite[] {
  const taken = blocked;
  blocked = [];

  return taken;
}

function toPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}
